//------ 特征离线包定义持久化 开始------
/**
 * assembly.json.extensions 中保存特征时间线的字段名。
 */
export const PUPPY_FEATURE_EXTENSION_KEY = 'features';

/**
 * 离线包特征扩展 schema 版本。
 */
export enum PuppyFeatureExtensionSchemaVersion {
    /** 第一版：完整 FeatureManager 命令时间线 + 退回控制棒状态。 */
    V1 = 'puppy-feature-extension-v1',
}

/**
 * 特征时间线快照记录（结构透传）。
 *
 * 设计说明：
 * 1. `commands` 是 FeatureRollbackController 的完整 CadCommand 时间线（plain data），
 *    其内部草图引用（binding.sketchId、contourSourceObjectIds、runtimeSketchEntities 等）
 *    已由采集门面归一为草图持久 ID，恢复门面再映射回新运行时 uuid；
 * 2. Worker 几何对象 objectId 在 `.puppy` 导入后保持不变，不参与归一化；
 * 3. 载荷不携带墙上时间等非确定值（命令 timestamp 是创建时刻的固定存量值，随命令存续）。
 */
export interface PuppyFeatureRollbackSnapshotRecord {
    /** 采集时的文档标识（固定协议值，不参与恢复语义）。 */
    readonly documentId: string;
    /** 完整特征命令时间线。 */
    readonly commands: readonly Record<string, unknown>[];
    /** 退回控制棒位置。 */
    readonly cursorIndex: number;
    /** 上一次退回控制棒位置；从未退回过时为 null（与 FeatureRollbackController 初始态一致）。 */
    readonly previousCursorIndex: number | null;
    /** 特征/草图/基准混排时间线条目。 */
    readonly timelineItems: readonly Record<string, unknown>[];
}

/**
 * 离线包特征扩展载荷。
 */
export interface PuppyFeatureExtension {
    /** 扩展 schema 版本。 */
    readonly schemaVersion: PuppyFeatureExtensionSchemaVersion;
    /** 特征时间线快照。 */
    readonly snapshot: PuppyFeatureRollbackSnapshotRecord;
}

/** 采集门面产出的扩展输入（即快照记录本身）。 */
export type PuppyFeatureExtensionInput = PuppyFeatureRollbackSnapshotRecord;

/**
 * 判断未知值是否为普通记录对象。
 * @param value 未知值。
 * @returns 是否为可读记录对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

/**
 * 判断值是否为可序列化 plain JSON 数据。
 * @param value 未知值。
 * @returns 是否合法。
 */
function isSerializablePlainData(value: unknown): boolean {
    if (value === null || typeof value === 'string' || typeof value === 'boolean') {
        return true;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }
    if (Array.isArray(value)) {
        return value.every(isSerializablePlainData);
    }
    if (!isRecord(value)) {
        return false;
    }
    return Object.values(value).every((item) => item === undefined || isSerializablePlainData(item));
}

/**
 * 校验并深拷贝快照记录。
 * @param value 未知 JSON 值。
 * @param path 字段路径前缀。
 * @returns 已校验的快照记录。
 */
function parseSnapshotRecord(value: unknown, path: string): PuppyFeatureRollbackSnapshotRecord {
    if (!isRecord(value)) {
        throw new Error(`PuppyCAD 离线包特征扩展快照必须是对象: ${path}`);
    }
    if (typeof value.documentId !== 'string') {
        throw new Error(`PuppyCAD 离线包特征扩展字段必须是字符串: ${path}.documentId`);
    }
    if (typeof value.cursorIndex !== 'number' || !Number.isFinite(value.cursorIndex)) {
        throw new Error(`PuppyCAD 离线包特征扩展字段必须是有限数字: ${path}.cursorIndex`);
    }
    // previousCursorIndex 允许 null：控制器从未退回过时即为 null（JSON 净化也会把 undefined 归一为 null）。
    const rawPreviousCursorIndex: unknown = value.previousCursorIndex ?? null;
    if (rawPreviousCursorIndex !== null
        && (typeof rawPreviousCursorIndex !== 'number' || !Number.isFinite(rawPreviousCursorIndex))) {
        throw new Error(`PuppyCAD 离线包特征扩展字段必须是有限数字或 null: ${path}.previousCursorIndex`);
    }
    const previousCursorIndex = typeof rawPreviousCursorIndex === 'number' ? rawPreviousCursorIndex : null;
    const parseRecordList = (listValue: unknown, listPath: string): Record<string, unknown>[] => {
        if (!Array.isArray(listValue)) {
            throw new Error(`PuppyCAD 离线包特征扩展字段必须是数组: ${listPath}`);
        }
        return listValue.map((item, index) => {
            if (!isRecord(item) || !isSerializablePlainData(item)) {
                throw new Error(`PuppyCAD 离线包特征扩展条目必须是可序列化对象: ${listPath}[${index}]`);
            }
            return JSON.parse(JSON.stringify(item)) as Record<string, unknown>;
        });
    };
    const commands = parseRecordList(value.commands, `${path}.commands`);
    commands.forEach((command, index) => {
        if (typeof command.id !== 'string' || !command.id.trim()) {
            throw new Error(`PuppyCAD 离线包特征命令必须携带非空 id: ${path}.commands[${index}].id`);
        }
        if (typeof command.type !== 'string' || !command.type.trim()) {
            throw new Error(`PuppyCAD 离线包特征命令必须携带非空 type: ${path}.commands[${index}].type`);
        }
    });
    return {
        documentId: value.documentId,
        commands,
        cursorIndex: value.cursorIndex,
        previousCursorIndex,
        timelineItems: parseRecordList(value.timelineItems, `${path}.timelineItems`),
    };
}

/**
 * 根据快照输入创建离线包特征扩展。
 * @param input 特征时间线快照；无特征时可传 null/undefined。
 * @returns 有特征数据时返回扩展；没有数据时返回 `undefined`。
 */
export function createPuppyFeatureExtension(
    input: PuppyFeatureExtensionInput | null | undefined,
): PuppyFeatureExtension | undefined {
    if (!input || input.commands.length === 0) {
        return undefined;
    }
    return {
        schemaVersion: PuppyFeatureExtensionSchemaVersion.V1,
        snapshot: parseSnapshotRecord(input, 'snapshot'),
    };
}

/**
 * 解析 `.puppy` 特征扩展。
 * @param value 未知 JSON 值。
 * @returns 已校验的特征扩展。
 * @throws 当输入不是对象或扩展版本不受支持时。
 */
export function parsePuppyFeatureExtension(value: unknown): PuppyFeatureExtension {
    if (!isRecord(value)) {
        throw new Error('PuppyCAD 离线包特征扩展必须是对象');
    }
    if (value.schemaVersion !== PuppyFeatureExtensionSchemaVersion.V1) {
        throw new Error(`不支持的 PuppyCAD 离线包特征扩展版本: ${String(value.schemaVersion)}`);
    }
    return {
        schemaVersion: PuppyFeatureExtensionSchemaVersion.V1,
        snapshot: parseSnapshotRecord(value.snapshot, 'snapshot'),
    };
}

/**
 * 从 assembly.json extensions 中读取特征扩展。
 * @param extensions assembly.json.extensions。
 * @returns 存在时返回特征扩展；不存在时返回 `undefined`。
 */
export function readPuppyFeatureExtensionFromAssemblyExtensions(
    extensions: Readonly<Record<string, unknown>> | undefined,
): PuppyFeatureExtension | undefined {
    const value = extensions?.[PUPPY_FEATURE_EXTENSION_KEY];
    return value === undefined ? undefined : parsePuppyFeatureExtension(value);
}

/**
 * 把特征扩展写入 assembly.json extensions。
 * @param extensions 原始扩展字段。
 * @param featureExtension 特征扩展。
 * @returns 写入后的扩展字段；没有特征数据时返回原扩展副本。
 */
export function writePuppyFeatureExtensionToAssemblyExtensions(
    extensions: Readonly<Record<string, unknown>> | undefined,
    featureExtension: PuppyFeatureExtension | undefined,
): Record<string, unknown> | undefined {
    const nextExtensions = extensions ? {...extensions} : {};
    if (featureExtension) {
        nextExtensions[PUPPY_FEATURE_EXTENSION_KEY] = featureExtension;
    } else {
        delete nextExtensions[PUPPY_FEATURE_EXTENSION_KEY];
    }
    return Object.keys(nextExtensions).length > 0 ? nextExtensions : undefined;
}
//------ 特征离线包定义持久化 结束------
